import isPropValid from '@emotion/is-prop-valid';
import styledSystemPropTypes from '@styled-system/prop-types';
import { objectKeys } from '@trmediaab/zebra-utils';
import memoize from 'memoize-one';

let regex: RegExp | null = null;

const deepKeys = (obj: object, target: string[] = []) => {
  objectKeys(obj).forEach(key => {
    if (typeof obj[key] === 'object') {
      return deepKeys(obj[key], target);
    } else {
      target.push(key);
    }
  });

  return target;
};

// List of prop names that styled system supports.
// We could get this easier from @styled-system/should-forward-prop but
// that package has some @emotion deps that we don't want.
// Instead we go through the prop-types package and collect and use the
// keys from there.
const stProps = deepKeys(styledSystemPropTypes);

// List of our custom prop names
const customProps: string[] = [];

export const resetRegex = (): void => {
  regex = null;
};

export const addCustomProps = (propNames: string[]): void => {
  // Add to our list of custom props
  customProps.push(...propNames);
  // Reset memo cache so the memoized function is
  // called again next time. Reset regex so the
  // next call includes the new prop names.
  shouldForwardProp.clear();
  regex = null;
};

/**
 * Function passed to styled components for filtering out props we don't want styled components to
 * pass on to the underlying React node or the DOM element.
 * See
 * https://styled-components.com/docs/api#shouldforwardprop
 * https://github.com/styled-system/styled-system/blob/master/packages/should-forward-prop/src/index.js
 * https://github.com/styled-system/styled-system/issues/1253#issuecomment-619877917
 */
const shouldForwardProp = memoize((propName: string, target: unknown) => {
  if (regex == null) {
    const allProps = [...stProps, ...customProps];
    regex = new RegExp(`^(${allProps.join('|')})$`);
  }

  // Do not forward props that are in the list of custom styled system props
  if (regex.test(propName)) {
    return false;
  }

  if (typeof target === 'string') {
    // For HTML elements, forward the prop if it is a valid HTML attribute
    return isPropValid(propName);
  }

  // For other elements, forward all props
  return true;
});

export default shouldForwardProp;
